<template>
  <div class="invoice-processor">
    <h1 class="mb-4" v-if="!invoiceData">{{ $t('invoice_processor.title') }}</h1>
    <!-- Toast notification for failures with download link -->
    <div v-if="showToast" class="toast-notification position-fixed" style="top:1rem; right:1rem; z-index:1050; min-width:260px;">
      <div class="card shadow-sm">
        <div class="card-body p-2">
          <div class="d-flex align-items-start">
            <div class="me-2">
              <i class="bi bi-exclamation-triangle-fill text-warning"></i>
            </div>
            <div class="flex-grow-1 small">
              <div>{{ toastMessage }}</div>
              <div v-if="toastLink" class="mt-1">
                <a :href="toastLink" target="_blank" class="btn btn-sm btn-outline-secondary me-2">Download</a>
                <button class="btn btn-sm btn-outline-secondary" @click="copyToastLink">Copy URL</button>
              </div>
            </div>
            <button type="button" class="btn-close btn-close-sm ms-2" aria-label="Close" @click="hideToast" />
          </div>
        </div>
      </div>
    </div>

    <!-- Validation Popups -->
    <!-- Validation Popups -->
    <div class="validation-popups-container" v-if="invoiceData">
      <div v-if="totalAmountMismatch && !dismissedWarnings.totalAmount" class="validation-popup popup-red">
        <div class="d-flex align-items-center flex-grow-1">
          <i class="bi bi-exclamation-octagon-fill me-2"></i>
          {{ $t('invoice_processor.validation.total_mismatch') }}
          <span class="ms-2 small">({{ totalsObject.line_total.toFixed(2) }} vs {{ invoiceData.total_amount }})</span>
        </div>
        <button type="button" class="btn-close btn-close-white ms-2" aria-label="Close" @click="dismissedWarnings.totalAmount = true"></button>
      </div>
      <div v-if="netWeightMismatch && !dismissedWarnings.netWeight" class="validation-popup popup-yellow">
        <div class="d-flex align-items-center flex-grow-1">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          {{ $t('invoice_processor.validation.net_weight_mismatch') }}
          <span class="ms-2 small">({{ totalsObject.net_weight.toFixed(2) }} vs {{ invoiceData.total_net_Kg }})</span>
        </div>
        <button type="button" class="btn-close ms-2" aria-label="Close" @click="dismissedWarnings.netWeight = true"></button>
      </div>
      <div v-if="grossWeightMismatch && !dismissedWarnings.grossWeight" class="validation-popup popup-yellow">
        <div class="d-flex align-items-center flex-grow-1">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          {{ $t('invoice_processor.validation.gross_weight_mismatch') }}
          <span class="ms-2 small">({{ totalsObject.gross_weight.toFixed(2) }} vs {{ invoiceData.total_gross_Kg }})</span>
        </div>
        <button type="button" class="btn-close ms-2" aria-label="Close" @click="dismissedWarnings.grossWeight = true"></button>
      </div>
    </div>
    



<div class="row">
  <div class="col-md-12">
    <!-- Step 1: Upload PDF -->
    <div class="card mb-4" v-if="!invoiceData">
      <div class="card-header">
        <h5 class="mb-0">{{ $t('invoice_processor.upload_title') }}</h5>
      </div>
      <div class="card-body">
        <div v-if="error" class="alert alert-danger">
          <div>{{ error }}</div>
          <div v-if="failedDownloadLink" class="mt-2">
            <a :href="failedDownloadLink" target="_blank" class="btn btn-sm btn-outline-secondary">Download text sent to AI</a>
          </div>
        </div>
        


    <!-- Drag & Drop Area -->
    <div 
      class="drag-drop-area mb-3"
      :class="{ 'drag-over': isDragging }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div class="drag-drop-content">
        <i class="bi bi-cloud-arrow-up-fill"></i>
        <p>{{ $t('invoice_processor.drag_drop') }}</p>
        <p class="small text-muted">{{ $t('invoice_processor.or') }}</p>
        <label for="pdfFile" class="btn btn-outline-primary">
          {{ $t('invoice_processor.browse_btn') }}
        </label>
        <input 
          type="file" 
          class="form-control visually-hidden" 
          id="pdfFile" 
          accept=".pdf,.xlsx,.xls,.jpg,.jpeg,.png,image/*" 
          @change="handleFileChange"
          ref="fileInput"
        >
      </div>
      <div v-if="selectedFile" class="selected-file mt-3">
        <div class="d-flex align-items-center">
          <i class="bi bi-file-earmark-pdf me-2"></i>
          <span>{{ selectedFile.name }}</span>
          <button class="btn btn-sm btn-link text-danger ms-auto" @click.prevent="clearSelectedFile">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
    </div>
    
    <div class="form-text mb-3">
      {{ $t('invoice_processor.cost_info', { amount: creditsDisplay }) }}
    </div>
    

        <!-- Progress Bar -->
       <div v-if="processing" class="mb-3">
         <div class="progress" style="height: 25px;">
           <div 
             class="progress-bar progress-bar-striped progress-bar-animated" 
             role="progressbar" 
             :style="{ width: progressPercentage + '%' }"
             :aria-valuenow="progressPercentage"
             aria-valuemin="0" 
             aria-valuemax="100"
           >
             {{ progressPercentage }}% - {{ progressMessage }}
           </div>
         </div>
         <div class="text-center mt-2 small text-muted">
           Estimated time remaining: {{ estimatedTimeRemaining }}s
         </div>
       </div>

    <div class="text-center">
      <button 
        class="btn btn-primary px-4" 
        @click="handleProcessPdf" 
        :disabled="!selectedFile || processing || companyCredits <= 0"
      >
        <span v-if="processing" class="spinner-border spinner-border-sm me-2"></span>
        {{ $t('invoice_processor.process_btn') }}
      </button>
    </div>
  </div>
</div>

<!-- Purchase Modal (Reused or specific) -->
<!-- We can emit event or just redirect, but user wants redirect/page. -->
<!-- Simplest: Redirect to /credits which has the modal. -->
<!-- Or we can import the purchase modal. -->
<!-- For now, we'll use a confirm dialog or toast with action. -->

<!-- Confirm Upgrade Modal -->
<div class="modal fade" id="upgradeModal" tabindex="-1" ref="upgradeModal">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Daily Limit Reached</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>You have reached your daily free limit. Please upgrade to a paid plan or purchase credits to continue processing immediately.</p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        <button type="button" class="btn btn-primary" @click="goToCredits">View Options</button>
      </div>
    </div>
  </div>
</div>

<!-- Step 2: Edit Extracted Data -->
<div v-if="invoiceData">
  <!-- Header with filename and action buttons -->
  <!-- Header with filename and action buttons -->
  <div class="d-flex justify-content-between align-items-center mb-3">
    <div>
      <h3 class="mb-1">{{ $t('invoice_processor.title') }}</h3>
      <div class="text-muted small">
        <i class="bi bi-file-earmark-pdf me-1"></i>
        <span>{{ processedFileName || (invoiceData && invoiceData.filename) || 'Unknown File' }}</span>
      </div>
    </div>
    <div>
      <button class="btn btn-outline-secondary btn-sm me-2" @click="resetProcessor">
        {{ $t('invoice_processor.process_new_btn') }}
      </button>
      <button class="btn btn-outline-primary btn-sm me-2" @click="exportToExcel">
        {{ $t('invoice_processor.export_excel') }}
      </button>
      <button class="btn btn-outline-primary btn-sm" @click="exportToXml">
        {{ $t('invoice_processor.export_xml') }}
      </button>
    </div>
  </div>
  

   <!-- Compact Layout for Vendor, Buyer, and Invoice Details -->


  <div class="card mb-3">
    <div class="card-body p-3">
      <div class="row g-2">
        <!-- Left Column: Vendor Info -->
        <div class="col-md-3">
          <div class="card h-100 border-0">
            <div class="card-header bg-body-tertiary py-1">{{ $t('invoice_processor.vendor_info') }}</div>
            <div class="card-body p-2">
              <input 
                type="text" 
                class="form-control form-control-sm mb-2" 
                v-model="invoiceData.vendor_name"
                placeholder="Vendor Name"
              >
              <textarea 
                class="form-control form-control-sm mb-2" 
                v-model="invoiceData.vendor_adress" 
                rows="6"
                placeholder="Vendor Address"
              ></textarea>
              <input 
                type="text" 
                class="form-control form-control-sm" 
                v-model="invoiceData.vendor_VAT_ID"
                placeholder="Vendor VAT ID"
              >
            </div>
          </div>
        </div>
        
        <!-- Middle Column: Buyer Info -->
        <div class="col-md-3">
          <div class="card h-100 border-0">
            <div class="card-header bg-body-tertiary py-1">{{ $t('invoice_processor.buyer_info') }}</div>
            <div class="card-body p-2">
              <input 
                type="text" 
                class="form-control form-control-sm mb-2" 
                v-model="invoiceData.buyer_name"
                placeholder="Buyer Name"
              >
              <textarea 
                class="form-control form-control-sm mb-2" 
                v-model="invoiceData.buyer_adress" 
                rows="6"
                placeholder="Buyer Address"
              ></textarea>
              <input 
                type="text" 
                class="form-control form-control-sm" 
                v-model="invoiceData.buyer_VAT_ID"
                placeholder="Buyer VAT ID"
              >
            </div>
          </div>
        </div>
        
        <!-- Right Column: Invoice Details -->
        <div class="col-md-3">
          <div class="card h-100 border-0">
            <div class="card-header bg-body-tertiary py-1">{{ $t('invoice_processor.invoice_details') }}</div>
            <div class="card-body p-2">
              <div class="row g-2 mb-2">
                <div class="col-3 col-form-label-sm text-end">Invoice #:</div>
                <div class="col-9">
                  <div class="input-group input-group-sm">
                    <input type="text" class="form-control" v-model="invoiceData.invoice_number">
                    <span class="input-group-text">Date:</span>
                    <input type="text" class="form-control" v-model="invoiceData.invoice_date">
                  </div>
                </div>
              </div>
              
              <div class="row g-2 mb-2">
                <div class="col-3 col-form-label-sm text-end">PO #:</div>
                <div class="col-9">
                  <div class="input-group input-group-sm">
                    <input type="text" class="form-control" v-model="invoiceData.po_number">
                    <span class="input-group-text">Date:</span>
                    <input type="text" class="form-control" v-model="invoiceData.po_date">
                  </div>
                </div>
              </div>
              
              <div class="row g-2 mb-2">
                <div class="col-3 col-form-label-sm text-end">Amount:</div>
                <div class="col-9">
                  <div class="input-group input-group-sm">
                    <input type="text" class="form-control" v-model="invoiceData.total_amount">
                    <span class="input-group-text">Curr:</span>
                    <input type="text" class="form-control" v-model="invoiceData.currency">
                  </div>
                </div>
              </div>
              <div class="row g-2 mb-2">
                <div class="col-3 col-form-label-sm text-end">Netto:</div>
                <div class="col-9">
                  <div class="input-group input-group-sm">
                    <input type="text" class="form-control" v-model="invoiceData.total_net_Kg">
                    <span class="input-group-text">Kg</span>
                    <button class="btn btn-outline-secondary" type="button" @click="distributeNetWeight">Distribute</button>
                  </div>
                </div>
              </div>
              <div class="row g-2 mb-2">
                <div class="col-3 col-form-label-sm text-end">Brutto:</div>
                <div class="col-9">
                  <div class="input-group input-group-sm">
                    <input type="text" class="form-control" v-model="invoiceData.total_gross_Kg">
                    <span class="input-group-text">Kg</span>
                    <button class="btn btn-outline-secondary" type="button" @click="distributeGrossWeight">Distribute</button>
                  </div>
                </div>
              </div>                  
              <div class="row g-2">
                <div class="col-3 col-form-label-sm text-end">Incoterm:</div>
                <div class="col-9">
                  <input type="text" class="form-control form-control-sm" v-model="invoiceData.delivery_terms">
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Fourth Column: Additional Costs -->
        <div class="col-md-3">
          <div class="card h-100 border-0">
            <div class="card-header bg-body-tertiary py-1">{{ $t('invoice_processor.additional_costs') }}</div>
            <div class="card-body p-2">
              <div class="d-flex flex-column gap-2 mb-3">
                <!-- Header Row -->
                <div class="row g-2 mb-1 text-center">
                  <div class="col-sm-3"></div> <!-- Label column placeholder -->
                  <div class="col-sm-6 d-flex">
                    <div style="width: 65%" class="text-center pe-1"><small class="fw-bold">Value</small></div>
                    <div style="width: 35%" class="text-center ps-1"><small class="fw-bold">Curr</small></div>
                  </div>
                  <div class="col-sm-3"><small class="fw-bold">Val / Kg</small></div>
                </div>

                <!-- Transport -->
                <div class="row g-2 align-items-center">
                  <div class="col-sm-3">
                    <label class="col-form-label col-form-label-sm fw-bold">{{ $t('invoice_processor.costs.transport') }}</label>
                  </div>
                  <div class="col-sm-6 d-flex">
                    <div style="width: 65%" class="pe-1">
                      <input type="number" class="form-control form-control-sm no-spinners" v-model.number="additionalCosts.transport.value" placeholder="0.00">
                    </div>
                    <div style="width: 35%" class="ps-1">
                      <input type="text" class="form-control form-control-sm text-center" v-model="additionalCosts.transport.currency" placeholder="">
                    </div>
                  </div>
                  <div class="col-sm-3 d-flex justify-content-center gap-2">
                    <div class="form-check m-0">
                        <input class="form-check-input" type="radio" v-model="additionalCosts.transport.mode" value="value" id="transportVal">
                    </div>
                    <div class="form-check m-0">
                        <input class="form-check-input" type="radio" v-model="additionalCosts.transport.mode" value="weight" id="transportWei">
                    </div>
                  </div>
                </div>

                <!-- Asigurare -->
                <div class="row g-2 align-items-center">
                  <div class="col-sm-3">
                    <label class="col-form-label col-form-label-sm fw-bold">{{ $t('invoice_processor.costs.insurance') }}</label>
                  </div>
                  <div class="col-sm-6 d-flex">
                    <div style="width: 65%" class="pe-1">
                      <input type="number" class="form-control form-control-sm no-spinners" v-model.number="additionalCosts.asigurare.value" placeholder="0.00">
                    </div>
                    <div style="width: 35%" class="ps-1">
                      <input type="text" class="form-control form-control-sm text-center" v-model="additionalCosts.asigurare.currency" placeholder="">
                    </div>
                  </div>
                  <div class="col-sm-3 d-flex justify-content-center gap-2">
                    <div class="form-check m-0">
                        <input class="form-check-input" type="radio" v-model="additionalCosts.asigurare.mode" value="value" id="asigurareVal">
                    </div>
                    <div class="form-check m-0">
                        <input class="form-check-input" type="radio" v-model="additionalCosts.asigurare.mode" value="weight" id="asigurareWei">
                    </div>
                  </div>
                </div>

                <!-- Altele -->
                <div class="row g-2 align-items-center">
                  <div class="col-sm-3">
                    <label class="col-form-label col-form-label-sm fw-bold">{{ $t('invoice_processor.costs.other') }}</label>
                  </div>
                  <div class="col-sm-6 d-flex">
                    <div style="width: 65%" class="pe-1">
                      <input type="number" class="form-control form-control-sm no-spinners" v-model.number="additionalCosts.altele.value" placeholder="0.00">
                    </div>
                    <div style="width: 35%" class="ps-1">
                      <input type="text" class="form-control form-control-sm text-center" v-model="additionalCosts.altele.currency" placeholder="">
                    </div>
                  </div>
                  <div class="col-sm-3 d-flex justify-content-center gap-2">
                    <div class="form-check m-0">
                        <input class="form-check-input" type="radio" v-model="additionalCosts.altele.mode" value="value" id="alteleVal">
                    </div>
                    <div class="form-check m-0">
                        <input class="form-check-input" type="radio" v-model="additionalCosts.altele.mode" value="weight" id="alteleWei">
                    </div>
                  </div>
                </div>

                <!-- Deduceri -->
                <div class="row g-2 align-items-center">
                  <div class="col-sm-3">
                    <label class="col-form-label col-form-label-sm fw-bold">{{ $t('invoice_processor.costs.deductions') }}</label>
                  </div>
                  <div class="col-sm-6 d-flex">
                    <div style="width: 65%" class="pe-1">
                      <input type="number" class="form-control form-control-sm no-spinners" v-model.number="additionalCosts.deduceri.value" placeholder="0.00">
                    </div>
                    <div style="width: 35%" class="ps-1">
                      <input type="text" class="form-control form-control-sm text-center" v-model="additionalCosts.deduceri.currency" placeholder="">
                    </div>
                  </div>
                  <div class="col-sm-3 d-flex justify-content-center gap-2">
                    <div class="form-check m-0">
                        <input class="form-check-input" type="radio" v-model="additionalCosts.deduceri.mode" value="value" id="deduceriVal">
                    </div>
                    <div class="form-check m-0">
                        <input class="form-check-input" type="radio" v-model="additionalCosts.deduceri.mode" value="weight" id="deduceriWei">
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Single Action Button -->
              <div class="d-grid gap-2">
               <!--  <button class="btn btn-primary" type="button" @click="distributeAllCosts">  -->
                <button class="btn btn-sm btn-outline-primary me-2" type="button" @click="distributeAllCosts">
                  {{ $t('invoice_processor.costs.distribute_all') }}
                </button>
              </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  
  
  <!-- Line Items with Wijmo FlexGrid -->
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h5 class="mb-0">{{ $t('invoice_processor.line_items') }}</h5>
      <div>
        <button class="btn btn-sm btn-outline-primary me-2" @click="addLineItem">
          {{ $t('invoice_processor.add_item') }}
        </button>
        <button class="btn btn-sm btn-outline-secondary me-2" @click="refreshGrid">
          <i class="bi bi-arrow-clockwise"></i> {{ $t('invoice_processor.refresh') }}
        </button>
        <button class="btn btn-sm btn-primary" @click="saveInvoiceData">
          {{ $t('invoice_processor.save_changes') }}
        </button>
      </div>
    </div>
    <div class="card-body">
      <!-- Wijmo FlexGrid -->
      <div ref="flexGrid" class="grid-container"></div>
    </div>
  </div>
  

</div>

  </div>
</div>
  </div>
  <!-- LRN Prompt Modal -->
  <div class="modal fade" id="lrnModal" tabindex="-1" ref="lrnModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Export XML</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <label class="form-label">Please enter the LRN number:</label>
          <input type="text" class="form-control mb-4" v-model="lrnInputValue" @keyup.enter="confirmExportXml" />
          
          <div v-if="exchangeRates && exchangeRates.length > 0">
            <h6 class="mb-2">Previous Month Exchange Rates (Additional Costs)</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered table-striped">
                <thead class="table-light">
                  <tr>
                    <th>Currency</th>
                    <th>Rate</th>
                    <th>Multiplier</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(rate, index) in exchangeRates" :key="rate.currency">
                    <td class="align-middle">{{ rate.currency }}</td>
                    <td>
                      <input type="number" step="any" class="form-control form-control-sm" v-model="exchangeRates[index].rate" />
                    </td>
                    <td class="align-middle text-center">{{ rate.multiplier }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else-if="loadingRates" class="text-center my-3">
             <span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
             <span class="ms-2">Loading exchange rates...</span>
          </div>
          <div v-else class="text-muted small">No additional cost currencies found for exchange rates.</div>
          
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-primary" @click="confirmExportXml">Export</button>
        </div>
      </div>
    </div>
  </div>

</template>



<script>
import { mapGetters, mapActions } from 'vuex'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
// Wijmo is loaded as global scripts in index.html
// Access via window globals (same approach as eint2)
const wjCore = window.wijmo;
const wjGrid = window.wijmo.grid;
const wjInput = window.wijmo.input;
import * as bootstrap from 'bootstrap';
import axios from '@/axios'

export default {
  name: 'InvoiceProcessor',
  data() {
    return {
      selectedFile: null,
      processing: false,
      error: null,
      failedDownloadLink: null,
      // Toast state
      showToast: false,
      toastMessage: '',
      toastLink: null,
      toastTimeoutId: null,
      isDragging: false,
      lrnInputValue: '=year(YY)&user_settings.Declarant',
      lrnModalInstance: null,
      flexGrid: null,
      lineItemsCollection: null,
      processedFileName: '',
      deleteKeyHandler: null,
      autoSaveTimeout: null,
      totalsObject: {
        Serial_No: 'TOTALS',
        code: '',
        description: '',
        quantity: 0,
        unit_price: '',
        line_total: 0,
        net_weight: 0,
        gross_weight: 0,
        hsn_code: '',
        CO: '',
        VAT: '',
        dist_transport: 0,
        dist_asigurare: 0,
        dist_altele: 0,
        dist_deduceri: 0
      },
      // Progress bar data
      progressPercentage: 0,
      progressMessage: 'Initializing...',
      estimatedTimeRemaining: 0,
      progressInterval: null,
      statusInterval: null,
      startTime: null,
      estimatedTotalTime: 10,
      currentPhase: 0,
      phaseEstimates: {
        upload: 0,
        extraction: 0,
        ai_processing: 0
      },
      phaseDetails: {
        extraction_method: '',
        characters_extracted: 0,
        pages_extracted: 0,
        ai_pages_processed: 0,
        ai_characters_sent: 0
      }
      ,
      phaseRanges: {},
      // Track whether AI processing has completed (used to show waiting message when progress reaches 100%)
      aiProcessingFinished: false,
      
      // LRN Popup State
      exchangeRates: [],
      loadingRates: false,
      dismissedWarnings: {
        totalAmount: false,
        netWeight: false,
        grossWeight: false
      },
      additionalCosts: {
        transport: { value: 0, currency: 'EUR', mode: 'value' },
        asigurare: { value: 0, currency: 'EUR', mode: 'value' },
        altele: { value: 0, currency: 'EUR', mode: 'value' },
        deduceri: { value: 0, currency: 'EUR', mode: 'value' }
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['companyCredits', 'creditsDisplay']),
    ...mapGetters('settings', ['getSetting']),
    ...mapGetters('invoice', ['invoiceData', 'isProcessing']),
    
    totalAmountMismatch() {
      if (!this.invoiceData) return false;
      const setting = this.getSetting('check_values');
      const isEnabled = setting && (setting.value === 'YES' || setting.value === 'Yes');
      if (!isEnabled) return false;
      
      const total = parseFloat(this.invoiceData.total_amount) || 0;
      const sum = this.totalsObject.line_total || 0;
      return Math.abs(total - sum) > 1;
    },
    netWeightMismatch() {
      if (!this.invoiceData) return false;
      const setting = this.getSetting('check_weight');
      const isEnabled = setting && (setting.value === 'YES' || setting.value === 'Yes');
      if (!isEnabled) return false;
      
      const total = parseFloat(this.invoiceData.total_net_Kg) || 0;
      const sum = this.totalsObject.net_weight || 0;
      return Math.abs(total - sum) > 1;
    },
    grossWeightMismatch() {
      if (!this.invoiceData) return false;
      const setting = this.getSetting('check_weight');
      const isEnabled = setting && (setting.value === 'YES' || setting.value === 'Yes');
      if (!isEnabled) return false;
      
      const total = parseFloat(this.invoiceData.total_gross_Kg) || 0;
      const sum = this.totalsObject.gross_weight || 0;
      return Math.abs(total - sum) > 1;
    }
  },
  watch: {
    // NOTE: Removed deep: true from invoiceData watcher to prevent grid flickering
    // when line items are edited. The grid is already initialized and will update
    // through direct bindings and edit event handlers.
    // This watcher now only triggers when invoiceData is completely replaced
    invoiceData(newVal) {
      if (newVal && newVal.line_items && this.flexGrid) {
        // Only update if grid already exists; otherwise let initializeFlexGrid handle it
        console.log('[InvoiceProcessor] invoiceData replaced, updating totals');
        this.updateTotals();
      }
    },
    totalAmountMismatch(newVal) {
      if (!newVal) this.dismissedWarnings.totalAmount = false;
    },
    netWeightMismatch(newVal) {
      if (!newVal) this.dismissedWarnings.netWeight = false;
    },
    grossWeightMismatch(newVal) {
      if (!newVal) this.dismissedWarnings.grossWeight = false;
    },
    additionalCosts: {
      deep: true,
      handler() {
        this.debouncedSave();
      }
    }
  },
  mounted() {
    this.fetchSettings();
    
    this.upgradeModal = new bootstrap.Modal(this.$refs.upgradeModal);

    if (this.invoiceData && this.invoiceData.line_items) {
      this.$nextTick(() => {
        this.initializeFlexGrid();
      });
    }
  },
  updated() {
    if (this.invoiceData && this.invoiceData.line_items && !this.flexGrid) {
      this.$nextTick(() => {
        this.initializeFlexGrid();
      });
    }
  },
  methods: {
    ...mapActions('invoice', ['extractText', 'processInvoice', 'processPdf', 'updateInvoiceData', 'clearInvoiceData']),
    ...mapActions('settings', ['fetchSettings']),
    
    goToCredits() {
        // Hide modal first
        if (this.upgradeModal) this.upgradeModal.hide();
        this.$router.push('/credits');
    },
    
    // Calculate totals
    updateTotals() {
      if (!this.invoiceData || !this.invoiceData.line_items) {
        this.totalsObject.quantity = 0;
        this.totalsObject.line_total = 0;
        this.totalsObject.net_weight = 0;
        this.totalsObject.gross_weight = 0;
        this.totalsObject.dist_transport = 0;
        this.totalsObject.dist_asigurare = 0;
        this.totalsObject.dist_altele = 0;
        this.totalsObject.dist_deduceri = 0;
        return;
      }
      
      const totals = this.invoiceData.line_items.reduce((acc, item) => {
        acc.quantity += parseFloat(item.quantity) || 0;
        acc.line_total += parseFloat(item.line_total) || 0;
        acc.net_weight += parseFloat(item.net_weight) || 0;
        acc.gross_weight += parseFloat(item.gross_weight) || 0;
        acc.dist_transport += parseFloat(item.dist_transport) || 0;
        acc.dist_asigurare += parseFloat(item.dist_asigurare) || 0;
        acc.dist_altele += parseFloat(item.dist_altele) || 0;
        acc.dist_deduceri += parseFloat(item.dist_deduceri) || 0;
        return acc;
      }, {
        quantity: 0,
        line_total: 0,
        net_weight: 0,
        gross_weight: 0,
        dist_transport: 0,
        dist_asigurare: 0,
        dist_altele: 0,
        dist_deduceri: 0
      });
      
      this.totalsObject.quantity = totals.quantity;
      this.totalsObject.line_total = totals.line_total;
      this.totalsObject.net_weight = totals.net_weight;
      this.totalsObject.gross_weight = totals.gross_weight;
      this.totalsObject.dist_transport = totals.dist_transport;
      this.totalsObject.dist_asigurare = totals.dist_asigurare;
      this.totalsObject.dist_altele = totals.dist_altele;
      this.totalsObject.dist_deduceri = totals.dist_deduceri;
    },
    
    // Drag & Drop methods
    onDragOver(event) {
      this.isDragging = true;
    },
    
    onDragLeave(event) {
      this.isDragging = false;
    },
    
    onDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        const fileName = file.name.toLowerCase();
        if (file.type === 'application/pdf' || fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          this.selectedFile = file;
          this.error = null;
        } else {
          this.error = 'Please select a PDF or Excel file';
        }
      }
    },
    
    clearSelectedFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    handleFileChange(event) {
      console.log("File selected:", event.target.files[0]);
      this.selectedFile = event.target.files[0];
      this.error = null;
    },
    
    async handleProcessPdf() {
      console.log("Process document button clicked");
      if (!this.selectedFile) {
        console.log("No file selected");
        this.error = 'Please select a PDF or Excel file';
        return;
      }
      

      
      this.processing = true;
      this.error = null;
      this.progressPercentage = 0;
      this.progressMessage = 'Initializing...';
      this.estimatedTimeRemaining = 0;
      this.currentPhase = 0;
      // Reset phase details to avoid showing stale values from previous runs
      this.phaseDetails.extraction_method = '';
      this.phaseDetails.characters_extracted = 0;
      this.phaseDetails.pages_extracted = 0;
      this.phaseDetails.ai_pages_processed = 0;
      this.phaseDetails.ai_pages_processed = 0;
      this.phaseDetails.ai_characters_sent = 0;
      
      // Clear any existing progress interval
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
      
      try {
        // Store the filename for display
        this.processedFileName = this.selectedFile.name;
        
        // ============================================
        // STEP 1: Get phase estimates from backend
        // ============================================
        console.log(`Getting phase estimates for file (${this.selectedFile.size} bytes)`);
        this.progressMessage = 'Preparing...';
        
        const phasesResponse = await axios.post('/api/estimate-phases', {
          file_size: this.selectedFile.size
        });
        
        if (!phasesResponse.data.success) {
          throw new Error('Failed to get phase estimates');
        }
        
        this.phaseEstimates = phasesResponse.data.phases;
        this.estimatedTotalTime = phasesResponse.data.total_time;
        
        console.log('Phase estimates received:', this.phaseEstimates);
        console.log(`Total estimated time: ${this.estimatedTotalTime}s`);
        
        // Compute dynamic phase ranges (proportional to estimated times)
        const uploadTime = this.phaseEstimates.upload.time || 1;
        const extractionTime = this.phaseEstimates.extraction.time || 1;
        const aiTime = this.phaseEstimates.ai_processing.time || 1;
        const totalTime = uploadTime + extractionTime + aiTime || 1;
        // Ranges in percent
        const uploadPct = Math.round((uploadTime / totalTime) * 100);
        const extractionPct = Math.round((extractionTime / totalTime) * 100);
        const aiPct = 100 - uploadPct - extractionPct;

        let start = 0;
        this.phaseRanges = {
          1: { start: start, end: start + uploadPct },
          2: { start: start + uploadPct, end: start + uploadPct + extractionPct },
          3: { start: start + uploadPct + extractionPct, end: 100 }
        };

        // ============================================
        // STEP 2: Upload file (with real upload progress) then extraction
        // ============================================
        this.currentPhase = 1;
        this.phaseStartTime = Date.now();
        console.log('=== STEP 1: Uploading file ===');
        this.progressMessage = `Uploading file (${this.phaseEstimates.upload.details})...`;

        // start upload phase progress
        this.startPhaseProgress(1, uploadTime);

        const formData = new FormData();
        formData.append('file', this.selectedFile);

        let uploadCompleted = false;
        let extractResponse = null;
        try {
          const resp = await axios.post('/api/extract-text', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
            onUploadProgress: (progressEvent) => {
              const loaded = progressEvent.loaded || 0;
              const total = progressEvent.total || this.selectedFile.size || 1;
              const frac = Math.min(1, loaded / total);
              // map frac to phase 1 range
              const r = this.phaseRanges[1] || { start: 0, end: 10 };
              const pct = Math.round(r.start + (r.end - r.start) * frac);
              this.progressPercentage = pct;
              // update estimated remaining (upload + remaining phases)
              const remainingUpload = Math.max(0, uploadTime * (1 - frac));
              this.estimatedTimeRemaining = Math.round(remainingUpload + extractionTime + aiTime);

              if (frac >= 1 && !uploadCompleted) {
                uploadCompleted = true;
                if (this.progressInterval) { clearInterval(this.progressInterval); this.progressInterval = null; }
                // start extraction phase progress
                this.currentPhase = 2;
                this.phaseStartTime = Date.now();
                this.progressMessage = `Extracting text...`;
                this.startPhaseProgress(2, extractionTime);
              }
            }
          });
          extractResponse = resp.data;
        } catch (err) {
          if (this.progressInterval) { clearInterval(this.progressInterval); this.progressInterval = null; }
          throw err;
        }

        if (!extractResponse || !extractResponse.success) {
          throw new Error(extractResponse && extractResponse.error ? extractResponse.error : 'Failed to extract text');
        }

        // store extraction details
        this.phaseDetails.extraction_method = extractResponse.extraction_method || 'Unknown';
        this.phaseDetails.characters_extracted = extractResponse.char_count || 0;
        this.phaseDetails.pages_extracted = extractResponse.num_pages || 0;

        console.log(`Extraction method: ${this.phaseDetails.extraction_method}`);
        console.log(`Characters extracted: ${this.phaseDetails.characters_extracted}`);
        console.log(`Pages extracted: ${this.phaseDetails.pages_extracted}`);

        // finalize extraction phase
        await this.simulatePhase(2, extractionTime, true);
        if (this.progressInterval) { clearInterval(this.progressInterval); this.progressInterval = null; }

        this.progressMessage = `Text extracted! ${this.phaseDetails.characters_extracted} chars, ${this.phaseDetails.pages_extracted} pages using ${this.phaseDetails.extraction_method}`;
        await this.sleep(1500); // show details
        
        // ============================================
        // STEP 4: Process extracted text with AI
        // ============================================
        this.currentPhase = 3;
        this.phaseStartTime = Date.now();
        console.log('=== STEP 3: Processing with AI ===');

        // Limit max simulated progress to 99% until we get success response
        // Start AI phase progress interval BEFORE calling backend so UI shows AI progress
        this.progressMessage = `Processing with AI...`;
        this.aiProcessingFinished = false;
        this.startPhaseProgress(3, aiTime);

        // Start status polling
        this.statusInterval = setInterval(async () => {
          try {
            const statusResp = await axios.get('/api/processing-status');
            if (statusResp.data && statusResp.data.status === 'fallback') {
               this.progressMessage = `${statusResp.data.details} (This will take longer)`;
               // If we hit fallback, AI is working harder, ensure we don't finish early
               // Maybe extend simulation time? (Complex to do dynamically with current impl)
            }
          } catch (e) {
            console.error("Status check failed", e);
          }
        }, 2000);

        const processResponse = await this.processInvoice(extractResponse.extraction_id);
        
        // Stop status polling
        if (this.statusInterval) {
            clearInterval(this.statusInterval);
            this.statusInterval = null;
        }

        // Handle backend failure that provides a temp download URL for the sent text
        if (!processResponse.success) {
          if (processResponse.download_url) {
            // Make absolute URL if backend returned a relative path
            const url = (processResponse.download_url.startsWith('http')) ? processResponse.download_url : `${window.location.origin}${processResponse.download_url}`;
            this.failedDownloadLink = url;
            this.error = processResponse.error || 'Failed to extract invoice data. You can download the text sent to the AI for debugging.';
            this.processing = false;
            this.progressPercentage = 0;
            this.progressMessage = '';
            // stop any running progress interval
            if (this.progressInterval) { clearInterval(this.progressInterval); this.progressInterval = null; }
            // show transient toast with link
            this.showFailureToast(url);
            return;
          }
          throw new Error(processResponse.error);
        }

        // mark AI finished so progress sim stops showing the 'thinking' message
        this.aiProcessingFinished = true;
        
        // Store AI processing details
        if (processResponse.ai_processing_info) {
          this.phaseDetails.ai_pages_processed = processResponse.ai_processing_info.pages_processed || 0;
          this.phaseDetails.ai_characters_sent = processResponse.ai_processing_info.characters_sent_to_ai || 0;
        } else {
          this.phaseDetails.ai_pages_processed = processResponse.num_pages || 0;
          this.phaseDetails.ai_characters_sent = processResponse.char_count || 0;
        }
        
        console.log(`AI processing: ${this.phaseDetails.ai_pages_processed} pages, ${this.phaseDetails.ai_characters_sent} characters`);
        
        // Update progress message with AI details
        this.progressMessage = `Processing with AI (${this.phaseDetails.ai_pages_processed} pages, ${this.phaseDetails.ai_characters_sent} chars)...`;

        // Continue progress simulation for AI phase while processing (one last update)
        await this.simulatePhase(3, aiTime, true);
        if (this.progressInterval) { clearInterval(this.progressInterval); this.progressInterval = null; }
        
        // Show AI completion details
        this.progressMessage = `AI processed ${this.phaseDetails.ai_pages_processed} pages, ${this.phaseDetails.ai_characters_sent} characters`;
        await this.sleep(1500); // Show details for 1.5 seconds
        
        // ============================================
        // Complete
        // ============================================
        this.progressPercentage = 100;
        this.progressMessage = 'Complete!';
        this.estimatedTimeRemaining = 0;
        
        console.log("=== All phases complete ===");
        console.log(`Actual processing time: ${processResponse.processing_time}s`);
        console.log('[Process] invoiceData state:', this.invoiceData ? `${this.invoiceData.line_items?.length || 0} items` : 'null');
        
        // Stop progress interval
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
        
        this.processing = false;
        
        // Initialize FlexGrid after data is loaded (only if not already initialized)
        console.log('[Process] Scheduling grid initialization via $nextTick');
        this.$nextTick(() => {
          console.log('[Process] Inside $nextTick, calling initializeFlexGrid');
          console.log('[Process] invoiceData at grid init time:', this.invoiceData ? `${this.invoiceData.line_items?.length || 0} items` : 'null');
          if (!this.flexGrid) {
            this.initializeFlexGrid();
          } else {
            console.log('[Process] Grid already initialized, skipping re-init');
          }
        });
      } catch (error) {
        console.error("Error processing PDF:", error);
        this.error = error.message || 'Failed to process PDF. Please try again.';
        this.progressPercentage = 0;
        this.progressMessage = '';
        
        // Stop progress interval
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }

        if (this.statusInterval) {
          clearInterval(this.statusInterval);
          this.statusInterval = null;
        }
        console.error("Error in handleProcessPdf:", error);
        
        let errorMsg = error.message || "Unknown error";
        if (error.response && error.response.data && error.response.data.error) {
             errorMsg = error.response.data.error;
        }
        
        // Check for daily limit
        if (errorMsg.includes("Daily limit reached") || errorMsg.includes("Not enough credits")) {
             this.upgradeModal.show();
        }
        
        this.error = errorMsg;
        this.progressPercentage = 0;
        this.progressMessage = '';
        this.processing = false;
        
      }
    },
    // Simulate progress for a specific phase
    async simulatePhase(phaseNumber, estimatedTime, isResuming = false) {
      // Use dynamically computed phase ranges (set in handleProcessPdf)
      const ranges = this.phaseRanges || {1: {start:0,end:10}, 2: {start:10,end:50}, 3: {start:50,end:95}};
      const range = ranges[phaseNumber];
      const phaseDuration = Math.max(1, estimatedTime) * 1000; // ms
      const phaseStartTime = isResuming ? this.phaseStartTime : Date.now();
      const phaseElapsed = Math.max(0, Date.now() - phaseStartTime);
      const phaseProgress = Math.min(1, phaseElapsed / phaseDuration);

      // Overall progress mapped into phase range
      const phaseRangeSize = Math.max(1, range.end - range.start);
      const currentProgress = range.start + (phaseRangeSize * phaseProgress);

      this.progressPercentage = Math.min(range.end, Math.round(currentProgress));

      // Estimate remaining time as remaining in this phase plus remaining phases
      const uploadTime = (this.phaseEstimates && this.phaseEstimates.upload && this.phaseEstimates.upload.time) || 0;
      const extractionTime = (this.phaseEstimates && this.phaseEstimates.extraction && this.phaseEstimates.extraction.time) || 0;
      const aiTime = (this.phaseEstimates && this.phaseEstimates.ai_processing && this.phaseEstimates.ai_processing.time) || 0;
      const phaseTimes = [uploadTime, extractionTime, aiTime];
      let remaining = 0;
      for (let p = phaseNumber; p <= 3; p++) {
        if (p === phaseNumber) {
          remaining += Math.max(0, (phaseTimes[p-1] || 0) - (phaseElapsed / 1000));
        } else {
          remaining += (phaseTimes[p-1] || 0);
        }
      }
      this.estimatedTimeRemaining = Math.max(0, Math.round(remaining));

      const elapsedSec = (phaseElapsed / 1000).toFixed(1);
      const progressMsg = `Phase ${phaseNumber} progress: ${this.progressPercentage}% (elapsed: ${elapsedSec}s / estimated: ${estimatedTime}s)`;
      console.log(progressMsg);

      // If AI phase would reach its end but processing hasn't finished, show waiting message and chars sent
      if (phaseNumber === 3 && !this.aiProcessingFinished) {
        // if phase time exceeded or we've reached the phase end percent
        const exceededTime = (phaseElapsed / 1000) > estimatedTime + 1;
        const atPhaseEnd = this.progressPercentage >= (this.phaseRanges && this.phaseRanges[3] ? this.phaseRanges[3].end : 100);
        if (exceededTime || atPhaseEnd) {
          const chars = this.phaseDetails.ai_characters_sent || this.phaseDetails.characters_extracted || 0;
          this.progressPercentage = 100;
          this.progressMessage = `Hard job, AI is still thinking... Sent ${chars} characters for processing`;
          this.estimatedTimeRemaining = 0;
        }
      }
    },
    
    // Start phase-based progress tracking
    startPhaseProgress(phaseNumber, estimatedTime) {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
      }

      // ensure phaseStartTime set
      this.phaseStartTime = Date.now();
      this.progressInterval = setInterval(() => {
        this.simulatePhase(phaseNumber, estimatedTime, true);

        // If this phase appears to be done, stop interval for a short moment
        const elapsed = (Date.now() - this.phaseStartTime) / 1000;
        if (elapsed > estimatedTime + 2) {
          // let next phase start set its own interval
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
      }, 500);
    },
    
    // Helper function to sleep
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    
    // Old method removed - replaced with startPhaseProgress
    resetProcessor() {
      this.clearInvoiceData();
      this.selectedFile = null;
      this.error = null;
      this.processedFileName = '';
      
      // Reset additional costs
      this.additionalCosts = {
        transport: { value: 0, currency: 'EUR', mode: 'value' },
        asigurare: { value: 0, currency: 'EUR', mode: 'value' },
        altele: { value: 0, currency: 'EUR', mode: 'value' },
        deduceri: { value: 0, currency: 'EUR', mode: 'value' }
      };
      
      // Reset progress bar variables
      this.progressPercentage = 0;
      this.progressMessage = '';
      this.estimatedTimeRemaining = 0;
      this.estimatedTotalTime = 10;
      this.startTime = null;
      
      // Clear progress interval
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }

      if (this.statusInterval) {
        clearInterval(this.statusInterval);
        this.statusInterval = null;
      }
      
      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      });
      
      if (this.flexGrid) {
        // Remove event listener before disposing
        if (this.deleteKeyHandler) {
          // remove from both capture and bubble phases to ensure cleanup
          try { this.flexGrid.hostElement.removeEventListener('keydown', this.deleteKeyHandler); } catch(e) {}
          try { this.flexGrid.hostElement.removeEventListener('keydown', this.deleteKeyHandler, true); } catch(e) {}
          this.deleteKeyHandler = null;
        }
        this.flexGrid.dispose();
        this.flexGrid = null;
      }
    },

    // Toast helpers
    showFailureToast(url) {
      this.toastMessage = 'AI extraction failed — you can download the text that was sent for processing.';
      this.toastLink = url;
      this.showToast = true;
      // clear previous timeout
      if (this.toastTimeoutId) { clearTimeout(this.toastTimeoutId); this.toastTimeoutId = null; }
      // auto-hide after 12 seconds
      this.toastTimeoutId = setTimeout(() => { this.hideToast(); }, 12000);
    },
    hideToast() {
      this.showToast = false;
      this.toastMessage = '';
      this.toastLink = null;
      if (this.toastTimeoutId) { clearTimeout(this.toastTimeoutId); this.toastTimeoutId = null; }
    },
    async copyToastLink() {
      if (!this.toastLink) return;
      try {
        await navigator.clipboard.writeText(this.toastLink);
      } catch (e) {
        // fallback: create temporary input
        const tmp = document.createElement('input');
        tmp.value = this.toastLink;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
      }
    },
    
    // Wijmo FlexGrid methods
    initializeFlexGrid() {
      try {
        console.log('[Grid] initializeFlexGrid called');
        if (!this.invoiceData || !this.invoiceData.line_items) {
          console.warn('[Grid] Cannot initialize: invoiceData or line_items missing');
          return;
        }
        
        console.log(`[Grid] Initializing grid with ${this.invoiceData.line_items.length} line items`);
        
        // Restore additional costs from invoiceData if present
        const cards = ['transport', 'asigurare', 'altele', 'deduceri'];
        cards.forEach(key => {
          if (this.invoiceData[`additional_cost_${key}_value`] !== undefined) {
               this.additionalCosts[key].value = this.invoiceData[`additional_cost_${key}_value`];
          }
          if (this.invoiceData[`additional_cost_${key}_currency`] !== undefined) {
               this.additionalCosts[key].currency = this.invoiceData[`additional_cost_${key}_currency`];
          }
          if (this.invoiceData[`additional_cost_${key}_mode`] !== undefined) {
               this.additionalCosts[key].mode = this.invoiceData[`additional_cost_${key}_mode`];
          }
        });
        
        // Dispose old grid if it exists
        if (this.flexGrid) {
          console.log('[Grid] Disposing old grid instance');
          // Remove old event listener before disposing
          if (this.deleteKeyHandler) {
            try { this.flexGrid.hostElement.removeEventListener('keydown', this.deleteKeyHandler); } catch(e) {}
            try { this.flexGrid.hostElement.removeEventListener('keydown', this.deleteKeyHandler, true); } catch(e) {}
            this.deleteKeyHandler = null;
          }
          this.flexGrid.dispose();
          this.flexGrid = null;
        }
        
        console.log('[Grid] Creating CollectionView');
        this.lineItemsCollection = new wijmo.collections.CollectionView(this.invoiceData.line_items);
        
        // Calculate initial totals
        this.updateTotals();
        
        console.log('[Grid] Creating FlexGrid instance');
        // Initialize the FlexGrid with tab navigation enabled
        this.flexGrid = new wjGrid.FlexGrid(this.$refs.flexGrid, {
          itemsSource: this.lineItemsCollection,
          allowAddNew: true,
          allowDelete: false,  // Disable built-in delete, we use custom handler
          allowSorting: true,
          selectionMode: 'Row',
          keyActionTab: 'CycleOut',
          autoGenerateColumns: false,
          frozenColumns: 1,
          lazyRender: false,
          showMarquee: true,
          alternatingRowStep: 1,
          columns: [
            { binding: 'Serial_No', header: 'Serial No', width: 80 },
            { binding: 'code', header: 'SKU', width: 100 },
            { binding: 'description', header: 'Description', width: 200 },
            { binding: 'quantity', header: 'Qty', width: 70, format: 'n0', isRequired: true, aggregate: 'Sum' },
            { binding: 'unit_price', header: 'Unit Price', width: 90, format: 'n2', isRequired: true },
            { binding: 'line_total', header: 'Line Total', width: 90, format: 'n2', isReadOnly: false, aggregate: 'Sum' },
            { binding: 'net_weight', header: 'Net [Kg]', width: 90, format: 'n2', isReadOnly: false, aggregate: 'Sum' },
            { binding: 'gross_weight', header: 'Gross [Kg]', width: 90, format: 'n2', isReadOnly: false, aggregate: 'Sum' },
            
            { binding: 'hsn_code', header: 'NC code', width: 90 },
            { binding: 'CO', header: 'CO', width: 70 },
            { binding: 'VAT', header: 'VAT', width: 70 },
            
            // New Columns (Moved to end)
            { binding: 'dist_transport', header: 'Transport', width: 90, format: 'n2', isReadOnly: false, aggregate: 'Sum' },
            { binding: 'dist_asigurare', header: 'Asigurare', width: 90, format: 'n2', isReadOnly: false, aggregate: 'Sum' },
            { binding: 'dist_altele', header: 'Altele', width: 90, format: 'n2', isReadOnly: false, aggregate: 'Sum' },
            { binding: 'dist_deduceri', header: 'Deduceri', width: 90, format: 'n2', isReadOnly: false, aggregate: 'Sum' }
          ],
          cellEditEnded: (s, e) => {
            try {
              const item = s.rows[e.row].dataItem;
              if (e.col === s.columns.getColumn('quantity').index || 
                  e.col === s.columns.getColumn('unit_price').index) {
                item.line_total = (item.quantity || 0) * (item.unit_price || 0);
                this.updateTotals();
                s.refresh();
              } else {
                 // For any other edits (like distributed values), update totals
                 this.updateTotals();
              }
            } catch (err) {
              console.error('[Grid] Error in cellEditEnded:', err);
            }
          }
        });
        
        console.log('[Grid] Adding column footers');
        // Add column footers for totals
        this.flexGrid.columnFooters.rows.push(new wjGrid.Row(this.totalsObject));
           
           // Add keyboard handling: block Backspace from clearing rows when not editing,
           // and only allow Delete key to remove an entire row (with confirmation).
           // Store the handler so we can remove it later
           this.deleteKeyHandler = (e) => {
             const key = e.key;

             // If Backspace pressed while NOT editing or focused inside an input,
             // prevent it from deleting data in the grid. Allow Backspace while
             // editing a cell or when a real input/textarea has focus.
             if (key === 'Backspace') {
               const active = document.activeElement;
               const isInput = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable);

               // Detect whether the grid is currently in edit mode. Use activeEditor
               // or isEditing() if available.
               let isGridEditing = false;
               try {
                 isGridEditing = !!(this.flexGrid && (this.flexGrid.activeEditor || (typeof this.flexGrid.isEditing === 'function' && this.flexGrid.isEditing())));
               } catch (err) {
                 isGridEditing = !!(this.flexGrid && this.flexGrid.activeEditor);
               }

               // If the grid is not being edited and focus is not inside a real input,
               // block Backspace to avoid clearing multiple cells when a row is selected.
               if (!isGridEditing && !isInput) {
                 e.preventDefault();
                 e.stopPropagation();
               }
               // Do not proceed to delete-row logic for Backspace
               return;
             }

             // Only Delete key removes the whole row
             if (key === 'Delete' || key === 'Del') {
               // Prevent the default delete behavior
               e.preventDefault();
               e.stopPropagation();

               const selectedRow = this.flexGrid.selection.row;

               // Make sure we have a valid row selected
               if (selectedRow >= 0 && selectedRow < this.flexGrid.rows.length) {
                 const item = this.flexGrid.rows[selectedRow].dataItem;

                 // Don't delete if it's the totals row or undefined
                 if (!item || item === this.totalsObject) {
                   return;
                 }

                 // Show confirmation dialog
                 const itemDescription = item.description || item.code || 'this item';
                 if (confirm(`Are you sure you want to delete "${itemDescription}"?`)) {
                   // Remove from the collection (which will update the grid automatically)
                   this.lineItemsCollection.remove(item);
                   this.updateTotals();
                 }
               }
             }
           };

           // Add the event listener (use capture so we intercept before other handlers)
           this.flexGrid.hostElement.addEventListener('keydown', this.deleteKeyHandler, true);
        
        console.log('[Grid] Setting up collection change handler');
        this.lineItemsCollection.collectionChanged.addHandler((s, e) => {
          if (e.action === wijmo.collections.NotifyCollectionChangedAction.Add) {
            const newItem = e.item;
            if (!newItem.Serial_No) newItem.Serial_No = '';
            if (!newItem.code) newItem.code = '';
            if (!newItem.description) newItem.description = '';
            if (!newItem.CO) newItem.CO = '';
            if (!newItem.quantity) newItem.quantity = 0;
            if (!newItem.unit_price) newItem.unit_price = 0;
            if (!newItem.line_total) newItem.line_total = 0;
            if (!newItem.net_weight) newItem.net_weight = 0;
            if (!newItem.gross_weight) newItem.gross_weight = 0;
            if (!newItem.hsn_code) newItem.hsn_code = '';
            if (!newItem.VAT) newItem.VAT = '';
            
            if (!newItem.dist_transport) newItem.dist_transport = 0;
            if (!newItem.dist_asigurare) newItem.dist_asigurare = 0;
            if (!newItem.dist_altele) newItem.dist_altele = 0;
            if (!newItem.dist_deduceri) newItem.dist_deduceri = 0;
          }
          this.updateTotals();
        });
        
        console.log('[Grid] Grid initialization completed successfully');
      } catch (error) {
        console.error('[Grid] Fatal error during initialization:', error);
        this.error = `Grid initialization failed: ${error.message}`;
      }
    },
    
    refreshGrid() {
      try {
        console.log('[Grid] refreshGrid called');
        if (!this.flexGrid || !this.invoiceData || !this.invoiceData.line_items) {
          console.warn('[Grid] refreshGrid: grid or data not available');
          return;
        }
        
        this.updateTotals();
        console.log('[Grid] Creating new CollectionView for refresh');
        this.lineItemsCollection = new wijmo.collections.CollectionView(this.invoiceData.line_items);
        this.flexGrid.itemsSource = this.lineItemsCollection;
        
        // Clear and re-add footer
        this.flexGrid.columnFooters.rows.clear();
        this.flexGrid.columnFooters.rows.push(new wjGrid.Row(this.totalsObject));
        
        this.flexGrid.refresh();
        console.log('[Grid] refreshGrid completed successfully');
      } catch (error) {
        console.error('[Grid] Error during refreshGrid:', error);
      }
    },
    
    addLineItem() {
      if (!this.invoiceData.line_items) {
        this.invoiceData.line_items = [];
      }
      
      this.invoiceData.line_items.push({
        Serial_No: '',
        code: '',
        description: '',
        CO: '',
        quantity: 0,
        unit_price: 0,
        line_total: 0,
        net_weight: 0,
        gross_weight: 0,
        hsn_code: '',
        VAT: '',
        
        dist_transport: 0,
        dist_asigurare: 0,
        dist_altele: 0,
        dist_deduceri: 0
      });
      
      this.updateTotals();
      this.refreshGrid();
    },
    
    async saveInvoiceData(silent = false) {
      if (!this.invoiceData) return;
      try {
        // Persist additional costs to invoiceData header fields
        const cards = ['transport', 'asigurare', 'altele', 'deduceri'];
        cards.forEach(key => {
            this.invoiceData[`additional_cost_${key}_value`] = this.additionalCosts[key].value;
            this.invoiceData[`additional_cost_${key}_currency`] = this.additionalCosts[key].currency;
            this.invoiceData[`additional_cost_${key}_mode`] = this.additionalCosts[key].mode;
        });

        await this.updateInvoiceData(this.invoiceData);
        if (!silent) {
          alert('Invoice data saved successfully');
        } else {
          console.log('Invoice data auto-saved');
        }
      } catch (error) {
        console.error("Error saving invoice data:", error);
        this.error = error.message || 'Failed to save invoice data. Please try again.';
      }
    },
    
    debouncedSave() {
      if (this.autoSaveTimeout) {
        clearTimeout(this.autoSaveTimeout);
      }
      this.autoSaveTimeout = setTimeout(() => {
        this.saveInvoiceData(true);
      }, 1000);
    },
    
    async distributeAllCosts() {
      if (!this.invoiceData || !this.invoiceData.line_items || !this.invoiceData.line_items.length) {
        alert('No line items to distribute costs.');
        return;
      }

      // Check if we have any costs to distribute or clear
      const keys = Object.keys(this.additionalCosts);
      
      // Iterate through all cost types and apply distribution
      // We pass 'true' to suppress individual confirmation dialogs for 0 values
      // But we only want to clear if the user explicitly zeroes it out. 
      // Actually, for "Distribute All", the expectation is to sync everything.
      // So if value is 0, we clear.
      
      let distributedCount = 0;
      keys.forEach(key => {
        this.distributeCost(key, true);
        distributedCount++;
      });
      
      this.updateTotals();
      this.refreshGrid();
      
      // Auto-save changes after distribution
      await this.saveInvoiceData(true);
    },

    distributeCost(type, suppressConfirm = false) {
        if (!this.invoiceData || !this.invoiceData.line_items || !this.invoiceData.line_items.length) {
          if (!suppressConfirm) alert('No line items to distribute costs.');
          return;
        }
        
        const costConfig = this.additionalCosts[type];
        if (!costConfig) return;
        
        const totalCostCtx = parseFloat(costConfig.value) || 0;
        const mode = costConfig.mode; // 'value' or 'weight' (weight falls back to gross weight)
        
        // Check if currently has distributed values > 0
        const currentDistributedSum = this.invoiceData.line_items.reduce((sum, item) => sum + (parseFloat(item[`dist_${type}`]) || 0), 0);

        if (totalCostCtx === 0) {
            // If input is 0
            // If suppressConfirm is true (batch mode), we clear unconditionally if we are running this function
            // OR we can check if there's anything to clear to avoid work?
            // Let's just clear.
             if (suppressConfirm || currentDistributedSum === 0 || confirm(`Set ${type} to 0 across all lines?`)) {
                this.invoiceData.line_items.forEach(item => {
                    item[`dist_${type}`] = 0;
                });
                if (!suppressConfirm) {
                    this.updateTotals();
                    this.refreshGrid();
                }
             }
             return;
        }
        
        // Distribution Base
        let baseTotal = 0;
        let useValue = (mode === 'value');
        
        if (useValue) {
             // Sum of line_total
             baseTotal = this.invoiceData.line_items.reduce((sum, item) => sum + (parseFloat(item.line_total) || 0), 0);
        } else {
             // mode === 'weight' -> Gross Weight
             baseTotal = this.invoiceData.line_items.reduce((sum, item) => sum + (parseFloat(item.gross_weight) || 0), 0);
        }
        
        if (baseTotal <= 0) {
             if (!suppressConfirm) alert(`Cannot distribute based on ${mode}: Total base value is 0.`);
             return;
        }
        
        // Distribute
        let distributedSum = 0;
        const count = this.invoiceData.line_items.length;
        
        this.invoiceData.line_items.forEach((item, index) => {
            let ratio = 0;
            if (useValue) {
                ratio = (parseFloat(item.line_total) || 0) / baseTotal;
            } else {
                ratio = (parseFloat(item.gross_weight) || 0) / baseTotal;
            }
            
            let share = 0;
            // For last item, apply remainder to avoid rounding issues
            if (index === count - 1) {
                share = totalCostCtx - distributedSum;
            } else {
                share = parseFloat((totalCostCtx * ratio).toFixed(2));
            }
            
            // Adjust to 2 decimals
            share = parseFloat(share.toFixed(2));
            distributedSum += share;
            
            item[`dist_${type}`] = share;
        });
        
        if (!suppressConfirm) {
            this.updateTotals();
            this.refreshGrid();
        }
    },
    
    distributeNetWeight() {
      if (!this.invoiceData || !this.invoiceData.line_items || !this.invoiceData.line_items.length) {
        alert('No line items to distribute weight.');
        return;
      }
      
      const totalNet = parseFloat(this.invoiceData.total_net_Kg) || 0;
      
      if (totalNet <= 0) {
        alert('Total net weight must be greater than 0.');
        return;
      }
      
      // Calculate total line amount to use as base, or fallback to quantity
      // We MUST use the sum of line_items as base to ensure ratios sum to 1
      let baseTotal = this.invoiceData.line_items.reduce((sum, item) => sum + (parseFloat(item.line_total) || 0), 0);
      let useValue = true;
      
      // If sum of line totals is 0, fallback to sum of quantities
      if (baseTotal <= 0) {
        useValue = false;
        baseTotal = this.invoiceData.line_items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
      }
      
      if (baseTotal <= 0) {
        alert('Cannot distribute: Sum of Line Totals and Sum of Quantities are both 0.');
        return;
      }
      
      // Distribute
      let distributedSum = 0;
      const count = this.invoiceData.line_items.length;

      this.invoiceData.line_items.forEach((item, index) => {
        let ratio = 0;
        if (useValue) {
          const val = parseFloat(item.line_total) || 0;
          ratio = val / baseTotal;
        } else {
          const qty = parseFloat(item.quantity) || 0;
          ratio = qty / baseTotal;
        }
        
        let share = 0;
        // For last item, apply remainder to ensure partial sums match totalNet exactly
        if (index === count - 1) {
            share = totalNet - distributedSum;
        } else {
            share = parseFloat((totalNet * ratio).toFixed(2));
        }
        
        // Ensure reasonable precision (2 decimals), but for last item we just take the difference which might be arbitrary precision if we aren't careful.
        // Actually for weight usually 2 or 3 decimals is good. Let's stick to 2 for consistency with UI.
        // Wait, if we force toFixed(2) on last item, we might still miss the exact total if totalNet had more decimals?
        // Let's assume input totalNet is entered by user and we want to match it.
        share = parseFloat(share.toFixed(2));
        distributedSum += share;
        
        item.net_weight = share;
      });
      
      this.updateTotals();
      this.refreshGrid();
    },
    
    distributeGrossWeight() {
      if (!this.invoiceData || !this.invoiceData.line_items || !this.invoiceData.line_items.length) {
        alert('No line items to distribute weight.');
        return;
      }
      
      const totalGross = parseFloat(this.invoiceData.total_gross_Kg) || 0;
      
      if (totalGross <= 0) {
        alert('Total gross weight must be greater than 0.');
        return;
      }
      
      // Calculate total line amount to use as base
      let baseTotal = this.invoiceData.line_items.reduce((sum, item) => sum + (parseFloat(item.line_total) || 0), 0);
      let useValue = true;
      
      // If sum of line totals is 0, fallback to sum of quantities
      if (baseTotal <= 0) {
        useValue = false;
        baseTotal = this.invoiceData.line_items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
      }
      
      if (baseTotal <= 0) {
        alert('Cannot distribute: Sum of Line Totals and Sum of Quantities are both 0.');
        return;
      }
      
      // Distribute
      let distributedSum = 0;
      const count = this.invoiceData.line_items.length;
      
      this.invoiceData.line_items.forEach((item, index) => {
        let ratio = 0;
        if (useValue) {
          const val = parseFloat(item.line_total) || 0;
          ratio = val / baseTotal;
        } else {
          const qty = parseFloat(item.quantity) || 0;
          ratio = qty / baseTotal;
        }
        
        let share = 0;
        if (index === count - 1) {
            share = totalGross - distributedSum;
        } else {
            share = parseFloat((totalGross * ratio).toFixed(2));
        }
        
        share = parseFloat(share.toFixed(2));
        distributedSum += share;
        
        item.gross_weight = share;
      });
      
      this.updateTotals();
      this.refreshGrid();
    },
    
    exportToExcel() {
      if (!this.invoiceData) return;
      
      const wb = XLSX.utils.book_new();
      
      // Create header row with invoice header fields followed by line item fields
      const header = [
        'Invoice Number',
        'Invoice Date',
        'Vendor Name',
        'Vendor Address',
        'Vendor VAT ID',
        'Buyer Name',
        'Buyer Address',
        'Buyer VAT ID',
        'Currency',
        'Delivery Terms',
        'Total Amount',
        'Total Net Weight (Kg)',
        'Total Gross Weight (Kg)',
        'Serial No',
        'SKU',
        'Description',
        'Qty',
        'Unit Price',
        'Line Total',
        'Net Weight (Kg)',
        'Gross Weight (Kg)',
        'NC Code',
        'CO',
        'VAT',
        'Transport',
        'Asigurare',
        'Altele',
        'Deduceri'
      ];
      
      // Create data rows - each line item with header info repeated
      const dataRows = [];
      
      if (this.invoiceData.line_items && this.invoiceData.line_items.length > 0) {
        this.invoiceData.line_items.forEach(item => {
          dataRows.push([
            // Invoice header fields (repeated on each row)
            this.invoiceData.invoice_number || '',
            this.invoiceData.invoice_date || '',
            this.invoiceData.vendor_name || '',
            this.invoiceData.vendor_adress || '',
            this.invoiceData.vendor_VAT_ID || '',
            this.invoiceData.buyer_name || '',
            this.invoiceData.buyer_adress || '',
            this.invoiceData.buyer_VAT_ID || '',
            this.invoiceData.currency || '',
            this.invoiceData.delivery_terms || '',
            this.invoiceData.total_amount || '',
            this.invoiceData.total_net_Kg || '',
            this.invoiceData.total_gross_Kg || '',
            // Line item fields
            item.Serial_No || '',
            item.code || '',
            item.description || '',
            item.quantity || 0,
            item.unit_price || 0,
            item.line_total || 0,
            item.net_weight || 0,
            item.gross_weight || 0,
            item.hsn_code || '',
            item.CO || '',
            item.VAT || '',
            item.dist_transport || 0,
            item.dist_asigurare || 0,
            item.dist_altele || 0,
            item.dist_deduceri || 0
          ]);
        });
      }

      
      // Create single worksheet with header and data
      const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
      XLSX.utils.book_append_sheet(wb, ws, 'Invoice Data');
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const fileName = `Invoice_${this.invoiceData.invoice_number || 'Export'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(data, fileName);
    },
    
    async exportToXml() {
      if (!this.invoiceData) return;
      
      // Evaluate the expected LRN format: year(YY) & user_settings.Declarant
      const yearYY = new Date().getFullYear().toString().slice(-2);
      const declarantObj = this.getSetting('Declarant');
      const declarant = declarantObj && declarantObj.value ? declarantObj.value : '';
      
      this.lrnInputValue = `${yearYY}${declarant}`;
      this.exchangeRates = [];
      this.loadingRates = true;
      
      if (!this.lrnModalInstance) {
          this.lrnModalInstance = new bootstrap.Modal(this.$refs.lrnModal);
      }
      this.lrnModalInstance.show();
      
      // Fetch exchange rates for additional costs
      try {
        const currencies = new Set();
        // Extract dynamically whatever additional_cost_*_currency exists
        for (const [key, value] of Object.entries(this.invoiceData)) {
            if (key.startsWith('additional_cost_') && key.endsWith('_currency') && value) {
                currencies.add(value);
            }
        }
        
        const currenciesArr = Array.from(currencies);
        if (currenciesArr.length > 0) {
            const resp = await axios.post('/api/exchange-rates', { currencies: currenciesArr });
            if (resp.data.success && resp.data.rates) {
                this.exchangeRates = resp.data.rates;
            }
        }
      } catch (err) {
         console.error("Failed to fetch exchange rates:", err);
      } finally {
         this.loadingRates = false;
      }
    },
    
    async confirmExportXml() {
      if (this.lrnModalInstance) {
          this.lrnModalInstance.hide();
      }
      
      this.invoiceData.LRN = this.lrnInputValue;
      
      // Inject modified exchange rates back into invoiceData
      if (this.exchangeRates && this.exchangeRates.length > 0) {
        for (const er of this.exchangeRates) {
          // Find matching additional_cost headers to update multiplier and rate
          for (const key of Object.keys(this.invoiceData)) {
            if (key.startsWith('additional_cost_') && key.endsWith('_currency') && this.invoiceData[key] === er.currency) {
              const baseName = key.replace('_currency', '');
              this.invoiceData[`${baseName}_multiplier`] = er.multiplier;
              this.invoiceData[`${baseName}_rate`] = er.rate;
            }
          }
        }
      }
      
      try {
        const response = await axios.post('/api/export-xml', this.invoiceData, {
          responseType: 'blob' // Important for handling binary data (file download)
        });
        
        // Create a blob from the response data
        const blob = new Blob([response.data], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from header or default
        let fileName = `Invoice_${this.invoiceData.invoice_number || 'Export'}.xml`;
        const disposition = response.headers['content-disposition'];
        if (disposition && disposition.indexOf('attachment') !== -1) {
             const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
             if (matches != null && matches[1]) { 
                 fileName = matches[1].replace(/['"]/g, '');
             }
        }
        
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
      } catch (error) {
        console.error('Export to XML failed:', error);
        alert('Failed to export XML. See console for details.');
      }
    }
  }
}
</script>


<style>
.invoice-processor {
  padding: 20px;
}

.drag-drop-area {
  border: 2px dashed #ccc;
  border-radius: 5px;
  padding: 30px;
  text-align: center;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
  cursor: pointer;
}

.drag-drop-area.drag-over {
  border-color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
}

.drag-drop-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.drag-drop-content i {
  font-size: 3rem;
  color: #6c757d;
  margin-bottom: 10px;
}

.drag-drop-content p {
  margin-bottom: 5px;
}

.selected-file {
  background-color: #e9ecef;
  padding: 10px;
  border-radius: 5px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.grid-container {
  height: 400px;
  margin-bottom: 10px;
}

/* Wijmo FlexGrid Light Mode Styles (scoped to light theme only) */
html:not([data-bs-theme="dark"]) .wj-flexgrid {
  background-color: #fff !important;
  color: #212529 !important;
  border-color: #dee2e6 !important;
}

html:not([data-bs-theme="dark"]) .wj-header {
  background-color: #f8f9fa !important;
  color: #212529 !important;
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
}

html:not([data-bs-theme="dark"]) .wj-cell {
  background-color: #fff !important;
  color: #212529 !important;
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
}

html:not([data-bs-theme="dark"]) .wj-alt:not(.wj-header):not(.wj-group):not(.wj-state-selected):not(.wj-state-multi-selected) {
  background-color: #f8f9fa !important;
}

html:not([data-bs-theme="dark"]) .wj-state-selected,
html:not([data-bs-theme="dark"]) .wj-state-multi-selected {
  background-color: #0d6efd !important;
  color: #fff !important;
}

/* Light mode column footers (totals row) */
html:not([data-bs-theme="dark"]) .wj-colfooters,
html:not([data-bs-theme="dark"]) .wj-footer,
html:not([data-bs-theme="dark"]) .wj-flexgrid .wj-colfooters .wj-header,
html:not([data-bs-theme="dark"]) .wj-flexgrid .wj-footer .wj-cell {
  background-color: #e9ecef !important;
  color: #212529 !important;
  font-weight: bold !important;
}

html:not([data-bs-theme="dark"]) .wj-colfooters .wj-cell,
html:not([data-bs-theme="dark"]) .wj-colfooters .wj-header {
  background-color: #e9ecef !important;
  color: #212529 !important;
  font-weight: bold !important;
}

/* Dark Mode Overrides */
[data-bs-theme="dark"] .drag-drop-area {
  background-color: #2b3035;
  border-color: #495057;
  color: #f8f9fa;
}

[data-bs-theme="dark"] .drag-drop-area.drag-over {
  background-color: rgba(13, 110, 253, 0.2);
  border-color: #0d6efd;
}

[data-bs-theme="dark"] .selected-file {
  background-color: #343a40;
  color: #f8f9fa;
}

/* Wijmo Dark Mode Overrides */
[data-bs-theme="dark"] .wj-flexgrid {
  background-color: #212529;
  color: #dee2e6;
  border-color: #495057;
}

[data-bs-theme="dark"] .wj-header {
  background-color: #343a40 !important;
  color: #f8f9fa !important;
  border-right: 1px solid #495057;
  border-bottom: 1px solid #495057;
}

[data-bs-theme="dark"] .wj-cell {
  background-color: #212529 !important;
  color: #dee2e6 !important;
  border-right: 1px solid #495057;
  border-bottom: 1px solid #495057;
}

/* Alternate row styling */
[data-bs-theme="dark"] .wj-alt:not(.wj-header):not(.wj-group):not(.wj-state-selected):not(.wj-state-multi-selected) {
    background-color: #2b3035 !important;
}

/* Selected state */
[data-bs-theme="dark"] .wj-state-selected,
[data-bs-theme="dark"] .wj-state-multi-selected {
    background-color: #0d6efd !important;
    color: #fff !important;
}

/* Column footer overrides - aici culoare total */
[data-bs-theme="dark"] .wj-colfooters,
[data-bs-theme="dark"] .wj-footer,
html[data-bs-theme="dark"] .wj-flexgrid .wj-colfooters .wj-header,
html[data-bs-theme="dark"] .wj-flexgrid .wj-footer .wj-cell {
  background: #9e9e9e !important;
  color: #000 !important;
  font-weight: bold !important;
}

[data-bs-theme="dark"] .wj-colfooters .wj-cell,
[data-bs-theme="dark"] .wj-colfooters .wj-header,
html[data-bs-theme="dark"] .wj-flexgrid .wj-colfooters .wj-cell,
html[data-bs-theme="dark"] .wj-flexgrid .wj-colfooters .wj-header {
  background: #9e9e9e !important;
  color: #000 !important;
  font-weight: bold !important;
}

/* Input fields in grid */
[data-bs-theme="dark"] .wj-content.wj-input {
    background-color: #212529 !important;
    color: #f8f9fa !important;
}

</style>

<style scoped>
.validation-popups-container {
  position: fixed;
  top: 80px; /* Below navbar/headers */
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  pointer-events: none; /* Allow clicking through if needed, but box ensures text is selectable if pointer-events: auto on children */
}

.validation-popup {
  padding: 10px 20px;
  border-radius: 5px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.15);
  font-weight: 600;
  display: flex;
  align-items: center;
  animation: slideDown 0.3s ease-out;
  pointer-events: auto;
  min-width: 300px;
  justify-content: center;
}

.popup-red {
  background-color: #dc3545;
  color: white;
  border: 1px solid #b02a37;
}

.popup-yellow {
  background-color: #ffc107;
  color: #000;
  border: 1px solid #d39e00;
}

@keyframes slideDown {
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Dark mode support for popups if needed, though specific colors requested are usually consistent */

.no-spinners::-webkit-outer-spin-button,
.no-spinners::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-spinners {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>